import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Plus, Trash2, Edit3, Save, X, Package } from 'lucide-react';

const empty = () => ({ name: '', description: '', active: true, max_picks: 3, allowed_slugs: [] });

function Editor({ initial, products, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isNew = !form.id;

  const toggleSlug = (slug) => {
    setForm((f) => ({ ...f, allowed_slugs: (f.allowed_slugs || []).includes(slug) ? f.allowed_slugs.filter((s) => s !== slug) : [...(f.allowed_slugs || []), slug] }));
  };

  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, description: form.description || '', active: !!form.active, max_picks: Number(form.max_picks) || 1, allowed_slugs: form.allowed_slugs || [] };
      if (isNew) await api.post('/admin/wardrobe-releases', payload); else await api.put(`/admin/wardrobe-releases/${form.id}`, payload);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-3" style={{ background: 'rgba(75,24,56,0.55)' }} onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden max-h-[92vh] flex flex-col" style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} onClick={(e) => e.stopPropagation()} data-testid="release-editor">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <div><div className="h-eyebrow">{isNew ? 'New wardrobe release' : 'Edit release'}</div><div className="h-display text-2xl mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{form.name || 'Untitled'}</div></div>
          <button onClick={onClose} className="p-1" aria-label="Close" style={{ color: 'var(--heritage-plum)' }}><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <label><div className="h-eyebrow mb-1.5">Name</div><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }} data-testid="release-name" /></label>
          <label><div className="h-eyebrow mb-1.5">Description</div><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label><div className="h-eyebrow mb-1.5">Max picks per ambassador</div><input type="number" value={form.max_picks} onChange={(e) => setForm({ ...form, max_picks: e.target.value })} className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }} data-testid="release-max" /></label>
            <label><div className="h-eyebrow mb-1.5">Status</div><select value={form.active ? '1' : '0'} onChange={(e) => setForm({ ...form, active: e.target.value === '1' })} className="w-full rounded-md border px-3 py-2 text-sm bg-white" style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}><option value="1">Active</option><option value="0">Paused</option></select></label>
          </div>
          <div>
            <div className="h-eyebrow mb-2">Products ambassadors can choose from</div>
            <div className="text-[11px] opacity-70 mb-2" style={{ color: 'var(--heritage-plum)' }}>Leave all unticked to allow ALL shop products.</div>
            <div className="grid grid-cols-2 gap-1.5">
              {products.map((p) => (
                <label key={p.slug} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-[rgba(75,24,56,0.04)]">
                  <input type="checkbox" checked={(form.allowed_slugs || []).includes(p.slug)} onChange={() => toggleSlug(p.slug)} />
                  <span style={{ color: 'var(--heritage-plum)' }}>{p.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <button onClick={onClose} className="h-btn-outline text-xs">Cancel</button>
          <button onClick={save} disabled={saving} className="h-btn text-xs" data-testid="release-save"><Save size={12} className="mr-1" /> {saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminWardrobeReleases() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, p, pk] = await Promise.all([
        api.get('/admin/wardrobe-releases'),
        api.get('/admin/shop/products'),
        api.get('/admin/wardrobe-picks'),
      ]);
      setRows(r.data.releases || []); setProducts(p.data.products || []); setPicks(pk.data.picks || []);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete release "${r.name}"?`)) return;
    try { await api.delete(`/admin/wardrobe-releases/${r.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const dispatch = async (p) => {
    try { await api.post(`/admin/wardrobe-picks/${p.id}/dispatch`); toast.success('Marked dispatched'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const removePick = async (p) => {
    if (!window.confirm(`Delete this pick?`)) return;
    try { await api.delete(`/admin/wardrobe-picks/${p.id}`); toast.success('Removed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-wardrobe">
      <div className="h-panel-thin p-6 flex items-center justify-between">
        <div><div className="h-eyebrow">Ambassadors</div><div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Wardrobe Releases</div>
          <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Create a release, choose the products ambassadors can pick from, set a limit. Approve their picks below.</p></div>
        <button onClick={() => setEditing(empty())} className="h-btn text-xs" data-testid="release-new"><Plus size={12} className="mr-1" /> New release</button>
      </div>

      {loading && <div className="h-eyebrow">Loading…</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((r) => (
          <Panel key={r.id} className="p-4" data-testid={`release-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>{r.name}</div>
                <div className="text-xs opacity-75 mt-1" style={{ color: 'var(--heritage-plum)' }}>Max picks: {r.max_picks} · {(r.allowed_slugs || []).length || 'all'} product{(r.allowed_slugs || []).length === 1 ? '' : 's'}</div>
                {r.description && <p className="text-sm mt-2 opacity-90" style={{ color: 'var(--heritage-plum)' }}>{r.description}</p>}
              </div>
              <span className="h-chip text-[9px]" style={{ background: r.active ? 'rgba(91,110,44,0.18)' : 'rgba(154,44,44,0.15)', borderColor: 'transparent', color: 'var(--heritage-plum)' }}>{r.active ? 'ACTIVE' : 'PAUSED'}</span>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
              <button onClick={() => setEditing(r)} className="h-btn-outline text-xs"><Edit3 size={12} className="mr-1" /> Edit</button>
              <button onClick={() => remove(r)} className="text-xs inline-flex items-center gap-1 opacity-80 hover:opacity-100 px-2 py-1" style={{ color: '#9A2C2C' }}><Trash2 size={12} /></button>
            </div>
          </Panel>
        ))}
      </div>

      <Panel className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Package size={16} style={{ color: 'var(--heritage-plum)' }} />
          <div className="h-display text-2xl" style={{ color: 'var(--heritage-plum)' }}>Pending Picks</div>
        </div>
        {picks.length === 0 && <div className="text-sm opacity-70" style={{ color: 'var(--heritage-plum)' }}>No picks yet.</div>}
        <ul className="divide-y" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          {picks.map((p) => (
            <li key={p.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
              <div><div className="h-serif" style={{ color: 'var(--heritage-plum)' }}>{p.ambassador_name} · {p.slug}</div>
                <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>{[p.size && `Size ${p.size}`, p.colour].filter(Boolean).join(' · ')} · SKU {p.sku}</div>
                {p.notes && <div className="text-xs italic opacity-70 mt-0.5" style={{ color: 'var(--heritage-plum)' }}>Note: {p.notes}</div>}</div>
              <div className="flex items-center gap-2">
                <span className="h-chip text-[9px]" style={{ background: p.status === 'dispatched' ? 'rgba(75,24,56,0.2)' : 'rgba(75,24,56,0.08)', borderColor: 'transparent', color: 'var(--heritage-plum)' }}>{(p.status || 'pending').toUpperCase()}</span>
                {p.status !== 'dispatched' && <button onClick={() => dispatch(p)} className="h-btn text-xs" data-testid={`pick-dispatch-${p.id}`}>Mark dispatched</button>}
                <button onClick={() => removePick(p)} className="text-xs inline-flex items-center gap-1 opacity-80 hover:opacity-100 px-2 py-1" style={{ color: '#9A2C2C' }}><Trash2 size={12} /></button>
              </div>
            </li>
          ))}
        </ul>
      </Panel>

      {editing && <Editor initial={editing} products={products} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
